import { Box, Text } from '@factorialco/deprecated-design-system'
import {
  PerformanceProcessSettingsUpdateAgreementsDocument,
  PerformanceProcessSettingsUpdateCompetenciesAssessmentsDocument,
  PerformanceProcessSettingsUpdateEmployeePotentialScoreDocument,
  PerformanceProcessSettingsUpdateRepublishEvaluationsDocument,
  PerformanceReviewProcessSettingsFeaturesFragment,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import i18n from 'modules/core/lib/i18n'

import {
  SettingsFeatures,
  type TypeSettingsFeatures,
} from 'modules/performance_process/components/SettingsFeatures'

export const Features: React.FC<{
  review: PerformanceReviewProcessSettingsFeaturesFragment
  isRepublishFeatureEnabled: boolean
}> = ({ review, isRepublishFeatureEnabled }) => {
  const reviewIsFinished = review.status === 'finished'
  const reviewIsDraft = review.status === 'draft'

  const [updateAgreementsMutation, { loading: loadingAgreements }] = useMutation(
    PerformanceProcessSettingsUpdateAgreementsDocument,
    {
      extractorFn: (data) =>
        data.performanceMutations.updateAgreementsConfigurationPerformanceReviewProcess,
    }
  )

  const [updateEmployeePotentialScoreMutation, { loading: loadingEmployeePotentialScore }] =
    useMutation(PerformanceProcessSettingsUpdateEmployeePotentialScoreDocument, {
      extractorFn: (data) =>
        data.performanceMutations.updateEmployeePotentialScoreConfigurationPerformanceReviewProcess,
    })

  const [updateCompetenciesAssessmentsMutation, { loading: loadingCompetenciesAssessments }] =
    useMutation(PerformanceProcessSettingsUpdateCompetenciesAssessmentsDocument, {
      extractorFn: (data) =>
        data.performanceMutations
          .updateCompetenciesAssessmentsConfigurationPerformanceReviewProcess,
    })

  const [updateRepublishEvaluationsMutation, { loading: loadingRepublishEvaluations }] =
    useMutation(PerformanceProcessSettingsUpdateRepublishEvaluationsDocument, {
      extractorFn: (data) =>
        data.performanceMutations.updateRepublishEvaluationsConfigurationPerformanceReviewProcess,
    })

  const updateAgreements = (enabled: boolean) => {
    updateAgreementsMutation({
      variables: { agreementsEnabled: enabled, id: review.id },
      optimisticResponse: {
        performanceMutations: {
          updateAgreementsConfigurationPerformanceReviewProcess: {
            reviewProcess: {
              id: review.id,
              agreementsConfiguration: {
                enabled,
              },
            },
            errors: [],
          },
        },
      },
    })
  }

  const updateEmployeePotentialScore = (enabled: boolean) => {
    updateEmployeePotentialScoreMutation({
      variables: { employeePotentialScoreEnabled: enabled, id: review.id },
      optimisticResponse: {
        performanceMutations: {
          updateEmployeePotentialScoreConfigurationPerformanceReviewProcess: {
            reviewProcess: {
              id: review.id,
              employeePotentialScoreConfiguration: {
                enabled,
              },
            },
            errors: [],
          },
        },
      },
    })
  }

  const updateCompetenciesAssessments = (enabled: boolean) => {
    updateCompetenciesAssessmentsMutation({
      variables: { competenciesAssessmentsEnabled: enabled, id: review.id },
      optimisticResponse: {
        performanceMutations: {
          updateCompetenciesAssessmentsConfigurationPerformanceReviewProcess: {
            reviewProcess: {
              id: review.id,
              competenciesAssessmentsConfiguration: {
                enabled,
              },
            },
            errors: [],
          },
        },
      },
    })
  }

  const updateRepublishEvaluations = (enabled: boolean) => {
    updateRepublishEvaluationsMutation({
      variables: { republishEvaluationsEnabled: enabled, id: review.id },
      optimisticResponse: {
        performanceMutations: {
          updateRepublishEvaluationsConfigurationPerformanceReviewProcess: {
            reviewProcess: {
              id: review.id,
              republishEvaluationsConfiguration: {
                enabled,
              },
            },
            errors: [],
          },
        },
      },
    })
  }

  const handleFeatureToggle = (feature: keyof TypeSettingsFeatures, enabled: boolean) => {
    switch (feature) {
      case 'agreementsConfiguration':
        updateAgreements(enabled)
        break
      case 'employeePotentialScoreConfiguration':
        updateEmployeePotentialScore(enabled)
        break
      case 'competenciesAssessmentsConfiguration':
        updateCompetenciesAssessments(enabled)
        break
      case 'republishEvaluationsConfiguration':
        updateRepublishEvaluations(enabled)
        break
    }
  }

  return (
    <Box gap="s32">
      <Box gap="s16">
        <Text size="400" color="grey1000" weight="semibold">
          {i18n.t('performance_process.reviews.features.title')}
        </Text>
        <Text size="300">{i18n.t('performance_process.reviews.features.description')}</Text>
      </Box>
      <SettingsFeatures
        features={{
          agreementsConfiguration: review.agreementsConfiguration.enabled,
          employeePotentialScoreConfiguration: review.employeePotentialScoreConfiguration.enabled,
          competenciesAssessmentsConfiguration: review.competenciesAssessmentsConfiguration.enabled,
          republishEvaluationsConfiguration: review.republishEvaluationsConfiguration.enabled,
        }}
        disabled={{
          agreementsConfiguration: reviewIsFinished || loadingAgreements,
          employeePotentialScoreConfiguration: !reviewIsDraft || loadingEmployeePotentialScore,
          competenciesAssessmentsConfiguration: !reviewIsDraft || loadingCompetenciesAssessments,
          republishEvaluationsConfiguration: reviewIsFinished || loadingRepublishEvaluations,
        }}
        onFeatureToggled={handleFeatureToggle}
        isRepublishFeatureEnabled={isRepublishFeatureEnabled}
      />
    </Box>
  )
}
